/**
 * VH-126: TanStack Query hooks for training locations and scanning.
 *
 * Scanning mutations invalidate the location detail as well as the list,
 * because confirming a scan changes a place's inventory and its equipment
 * count at the same time.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trainingLocationsAPI } from '../services/trainingLocations'

export const locationKeys = {
  all: ['training-locations'],
  lists: () => [...locationKeys.all, 'list'],
  details: () => [...locationKeys.all, 'detail'],
  detail: (id) => [...locationKeys.details(), id],
  scan: (id) => [...locationKeys.all, 'scan', id],
}

// ---- Reads ---------------------------------------------------------------

export function useTrainingLocations() {
  return useQuery({
    queryKey: locationKeys.lists(),
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
    staleTime: 1000 * 30,
  })
}

export function useCarryOverOffers(locationId) {
  return useQuery({
    queryKey: [...locationKeys.detail(locationId), 'carry-over'],
    queryFn: () => trainingLocationsAPI.carryOverOffers(locationId).then((r) => r.data),
    enabled: Boolean(locationId),
  })
}

export function useApplyCarryOver(locationId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (exerciseIds) =>
      trainingLocationsAPI.applyCarryOver(locationId, exerciseIds).then((r) => r.data),
    onSuccess: () => {
      // Coverage and the offer list both change the moment a clone lands.
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId) })
    },
  })
}

export function useLocationCoverage(locationId) {
  return useQuery({
    queryKey: [...locationKeys.detail(locationId), 'coverage'],
    queryFn: () => trainingLocationsAPI.coverage(locationId).then((r) => r.data),
    enabled: Boolean(locationId),
  })
}

export function useTrainingLocation(locationId) {
  return useQuery({
    queryKey: locationKeys.detail(locationId),
    queryFn: () => trainingLocationsAPI.get(locationId).then((r) => r.data),
    enabled: Boolean(locationId),
  })
}

export function useScan(scanId, options = {}) {
  return useQuery({
    queryKey: locationKeys.scan(scanId),
    queryFn: () => trainingLocationsAPI.getScan(scanId).then((r) => r.data),
    enabled: Boolean(scanId),
    ...options,
  })
}

// ---- Places --------------------------------------------------------------

export function useCreateLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => trainingLocationsAPI.create(data).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: locationKeys.lists() }),
  })
}

export function useUpdateLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      trainingLocationsAPI.update(id, data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.id) })
    },
  })
}

export function useDeleteLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => trainingLocationsAPI.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: locationKeys.lists() }),
  })
}

// ---- Scanning ------------------------------------------------------------

export function useQuickScan(locationId) {
  return useMutation({
    mutationFn: (file) =>
      trainingLocationsAPI.quickScan(locationId, file).then((r) => r.data),
  })
}

export function useAddScanPhoto() {
  return useMutation({
    mutationFn: ({ scanId, file }) =>
      trainingLocationsAPI.addPhoto(scanId, file).then((r) => r.data),
  })
}

export function useConfirmScan(locationId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ scanId, acceptedIds, nameCorrections }) =>
      trainingLocationsAPI
        .confirmScan(scanId, acceptedIds, nameCorrections)
        .then((r) => r.data),
    onSuccess: () => {
      // Confirming changes both the inventory and the count on the list.
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      if (locationId) {
        queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId) })
      }
    },
  })
}

export function useDiscardScan() {
  return useMutation({
    mutationFn: (scanId) => trainingLocationsAPI.discardScan(scanId),
  })
}
